import type { Sip, SipHostInfo, SipRoutingSettingsInfo, ContainerProfileSipInterface } from '@sbclite/core'
import { configurationApi } from './configurationApi'
import { api } from './api'
import type { Certificate, CodecGroup, MediaRealm, PeerGroup, RegisteredDeliverySettings, RoutingRule, SipInterface, SipManipulation, SipPeer, TlsContext } from './configurationApi'
import type { ProfileAssignmentInfo, ProfileInfo } from '@sbclite/core/container/index'

type BackupContainerProfileInfo = ProfileInfo & { createdAt: string; updatedAt: string }
type BackupContainerProfileSipInterface = Omit<ContainerProfileSipInterface, 'createdAt'> & { createdAt: string }
type BackupContainerProfileAssignment = ProfileAssignmentInfo & { createdAt: string; updatedAt: string }

const BACKUP_COLLECTION_NAMES = [
  'certificates',
  'hosts',
  'tlsContexts',
  'registeredDeliverySettings',
  'sipManipulations',
  'codecGroups',
  'mediaRealms',
  'sipInterfaces',
  'peerGroups',
  'sipProfiles',
  'containerProfiles',
  'containerProfileSipInterfaces',
  'containerProfileAssignments',
  'sipPeers',
  'routingRules',
  'sipRoutingSettings',
] as const

interface BackupRestoreSummary {
  create: number
  update: number
  conflict: number
  blocked: number
}

interface BackupRestoreReportPayload {
  dryRun: boolean
  options: RestoreOperation['options']
  summary: BackupRestoreSummary
  entries: Array<{ collection: string; id: string; name: string; action: 'create' | 'update' | 'conflict' | 'blocked'; reason: string | null }>
  blockers: string[]
  readyToApply: boolean
}

// Backup interfaces
export interface ConfigurationBackup {
  id: string
  name: string
  description: string
  createdAt: string
  createdBy: string
  version: string
  environment: string
  size: number
  configurations: {
    certificates: Certificate[]
    tlsContexts: TlsContext[]
    registeredDeliverySettings: RegisteredDeliverySettings[]
    sipManipulations: SipManipulation[]
    codecGroups: CodecGroup[]
    mediaRealms: MediaRealm[]
    sipInterfaces: SipInterface[]
    peerGroups: PeerGroup[]
    sipProfiles: Sip.SipProfileInfo[]
    containerProfiles: Array<Omit<ProfileInfo, 'createdAt' | 'updatedAt'> & { createdAt: string; updatedAt: string }>
    containerProfileSipInterfaces: Array<Omit<ContainerProfileSipInterface, 'createdAt'> & { createdAt: string }>
    containerProfileAssignments: Array<Omit<ProfileAssignmentInfo, 'createdAt' | 'updatedAt'> & { createdAt: string; updatedAt: string }>
    sipPeers: SipPeer[]
    routingRules: RoutingRule[]
    hosts: SipHostInfo[]
    sipRoutingSettings: SipRoutingSettingsInfo[]
  }
  metadata: {
    totalConfigurations: number
    configurationTypes: string[]
    presentCollections?: string[]
    dependencies: string[]
    tags: string[]
  }
}

export interface BackupSchedule {
  id: string
  name: string
  description: string
  enabled: boolean
  frequency: 'hourly' | 'daily' | 'weekly' | 'monthly'
  time: string
  retentionDays: number
  includeTypes: string[]
  excludeTypes: string[]
  environment: string
  lastRun?: string
  nextRun: string
  status: 'active' | 'paused' | 'error'
}

export interface RestoreOperation {
  id: string
  backupId: string
  startedAt: string
  completedAt?: string
  status: 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number
  results: {
    total: number
    successful: number
    failed: number
    skipped: number
  }
  errors: Array<{
    type: string
    item: string
    error: string
  }>
  options: {
    overwriteExisting: boolean
    includeTypes: string[]
    excludeTypes: string[]
    dryRun: boolean
  }
}

const CURRENT_BACKUP_FORMAT_VERSION = '1.6.0'
const SUPPORTED_BACKUP_FORMAT_VERSIONS = new Set(['1.6.0', '1.5.0', '1.4.0', '1.3.0', '1.2.0', '1.1.0'])

export interface BackupValidationIssue {
  path: string
  message: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getStringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key]
  return typeof value === 'string' ? value : null
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : []
}

function collectPresentCollections(configurations: Record<string, unknown>): string[] {
  return BACKUP_COLLECTION_NAMES.filter((collection) => Object.prototype.hasOwnProperty.call(configurations, collection))
}

function normalizeSipProfile(value: unknown): Sip.SipProfileInfo | null {
  if (!isRecord(value)) {
    return null
  }

  const id = getStringField(value, 'id')
  const name = getStringField(value, 'name')
  const sessionTimerMode = value.sessionTimerMode === 'disabled' || value.sessionTimerMode === 'supported' || value.sessionTimerMode === 'required' ? value.sessionTimerMode : null
  const sessionRefresherPreference = value.sessionRefresherPreference === 'uas' || value.sessionRefresherPreference === 'uac' || value.sessionRefresherPreference === 'none' ? value.sessionRefresherPreference : null
  const digestQop = value.digestQop === 'auth' || value.digestQop === 'auth-int' ? value.digestQop : null
  const digestAlgorithm = value.digestAlgorithm === 'MD5' || value.digestAlgorithm === 'SHA-256' ? value.digestAlgorithm : null
  const srtpMode = value.srtpMode === 'disabled' || value.srtpMode === 'optional' || value.srtpMode === 'mandatory' ? value.srtpMode : null
  const sdpOutboundProfile = value.sdpOutboundProfile === 'passthrough' || value.sdpOutboundProfile === 'carrier-basic-rtp' || value.sdpOutboundProfile === null ? value.sdpOutboundProfile : null

  if (
    !id ||
    !name ||
    typeof value.enabled !== 'boolean' ||
    sessionTimerMode === null ||
    !('sessionExpiresDefaultSeconds' in value) ||
    !('sessionExpiresMinSeconds' in value) ||
    !('sessionExpiresMaxSeconds' in value) ||
    sessionRefresherPreference === null ||
    typeof value.sessionTimerRejectBelowMin !== 'boolean' ||
    typeof value.registrarEnabled !== 'boolean' ||
    !('registerExpiresDefaultSeconds' in value) ||
    !('registerExpiresMinSeconds' in value) ||
    !('registerExpiresMaxSeconds' in value) ||
    !('registerMaxContacts' in value) ||
    typeof value.registerAllowWildcardContact !== 'boolean' ||
    typeof value.registerRequireContact !== 'boolean' ||
    typeof value.digestAuthEnabled !== 'boolean' ||
    !('digestNonceLifetimeSeconds' in value) ||
    !('digestLockoutMaxAttempts' in value) ||
    !('digestLockoutWindowSeconds' in value) ||
    digestQop === null ||
    digestAlgorithm === null ||
    srtpMode === null ||
    typeof value.sdpStripIce !== 'boolean' ||
    !Array.isArray(value.cryptoSuites) ||
    typeof value.createdAt !== 'string' ||
    typeof value.updatedAt !== 'string'
  ) {
    return null
  }

  const cryptoSuites = Array.isArray(value.cryptoSuites) ? value.cryptoSuites.filter((entry): entry is string => typeof entry === 'string') : []

  return {
    id,
    name,
    description: getStringField(value, 'description'),
    enabled: value.enabled,
    sessionTimerMode,
    sessionExpiresDefaultSeconds: typeof value.sessionExpiresDefaultSeconds === 'number' || value.sessionExpiresDefaultSeconds === null ? value.sessionExpiresDefaultSeconds : null,
    sessionExpiresMinSeconds: typeof value.sessionExpiresMinSeconds === 'number' || value.sessionExpiresMinSeconds === null ? value.sessionExpiresMinSeconds : null,
    sessionExpiresMaxSeconds: typeof value.sessionExpiresMaxSeconds === 'number' || value.sessionExpiresMaxSeconds === null ? value.sessionExpiresMaxSeconds : null,
    sessionRefresherPreference,
    sessionTimerRejectBelowMin: value.sessionTimerRejectBelowMin,
    registrarEnabled: value.registrarEnabled,
    registerExpiresDefaultSeconds: typeof value.registerExpiresDefaultSeconds === 'number' || value.registerExpiresDefaultSeconds === null ? value.registerExpiresDefaultSeconds : null,
    registerExpiresMinSeconds: typeof value.registerExpiresMinSeconds === 'number' || value.registerExpiresMinSeconds === null ? value.registerExpiresMinSeconds : null,
    registerExpiresMaxSeconds: typeof value.registerExpiresMaxSeconds === 'number' || value.registerExpiresMaxSeconds === null ? value.registerExpiresMaxSeconds : null,
    registerMaxContacts: typeof value.registerMaxContacts === 'number' || value.registerMaxContacts === null ? value.registerMaxContacts : null,
    registerAllowWildcardContact: value.registerAllowWildcardContact,
    registerRequireContact: value.registerRequireContact,
    digestAuthEnabled: value.digestAuthEnabled,
    digestNonceLifetimeSeconds: typeof value.digestNonceLifetimeSeconds === 'number' || value.digestNonceLifetimeSeconds === null ? value.digestNonceLifetimeSeconds : null,
    digestLockoutMaxAttempts: typeof value.digestLockoutMaxAttempts === 'number' || value.digestLockoutMaxAttempts === null ? value.digestLockoutMaxAttempts : null,
    digestLockoutWindowSeconds: typeof value.digestLockoutWindowSeconds === 'number' || value.digestLockoutWindowSeconds === null ? value.digestLockoutWindowSeconds : null,
    digestQop,
    digestAlgorithm,
    srtpMode,
    sdpStripIce: value.sdpStripIce,
    sdpOutboundProfile,
    cryptoSuites,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
  }
}

function normalizeHost(value: unknown): SipHostInfo | null {
  if (!isRecord(value)) return null
  const { id, name, createdAt, updatedAt } = value
  if (typeof id !== 'string' || typeof name !== 'string' || typeof createdAt !== 'string' || typeof updatedAt !== 'string') return null
  return {
    id,
    name,
    domain: typeof value.domain === 'string' ? value.domain : null,
    externalIp: typeof value.externalIp === 'string' ? value.externalIp : null,
    certificateId: typeof value.certificateId === 'string' ? value.certificateId : null,
    isDefault: value.isDefault === true,
    createdAt,
    updatedAt,
  }
}

function normalizeContainerProfile(value: unknown): BackupContainerProfileInfo | null {
  if (!isRecord(value)) return null
  const { id, name, createdAt, updatedAt } = value
  if (typeof id !== 'string' || typeof name !== 'string' || typeof createdAt !== 'string' || typeof updatedAt !== 'string') return null
  if (value.role !== 'full' && value.role !== 'signaling' && value.role !== 'media' && value.role !== 'ingress' && value.role !== 'egress') return null
  if (typeof value.enabled !== 'boolean' || typeof value.priority !== 'number' || typeof value.maxConcurrentCalls !== 'number') return null
  return {
    id,
    name,
    role: value.role,
    enabled: value.enabled,
    priority: value.priority,
    maxConcurrentCalls: value.maxConcurrentCalls,
    maxCps: typeof value.maxCps === 'number' ? value.maxCps : null,
    constraints: isRecord(value.constraints) ? value.constraints as ProfileInfo['constraints'] : null,
    mediaRealmId: typeof value.mediaRealmId === 'string' ? value.mediaRealmId : null,
    createdAt,
    updatedAt,
  }
}

function normalizeContainerProfileSipInterface(value: unknown): BackupContainerProfileSipInterface | null {
  if (!isRecord(value)) return null
  const { id, profileId, sipInterfaceId, createdAt } = value
  if (typeof id !== 'string' || typeof profileId !== 'string' || typeof sipInterfaceId !== 'string' || typeof createdAt !== 'string') return null
  return { id, profileId, sipInterfaceId, createdAt }
}

function normalizeContainerProfileAssignment(value: unknown): BackupContainerProfileAssignment | null {
  if (!isRecord(value)) return null
  const { id, containerId, profileId, createdAt, updatedAt } = value
  if (typeof id !== 'string' || typeof containerId !== 'string' || typeof profileId !== 'string' || typeof createdAt !== 'string' || typeof updatedAt !== 'string' || typeof value.strictMode !== 'boolean') return null
  return { id, containerId, profileId, strictMode: value.strictMode, createdAt, updatedAt }
}

function normalizeSipRoutingSettings(value: unknown): SipRoutingSettingsInfo | null {
  if (!isRecord(value)) return null
  const { id, createdAt, updatedAt } = value
  if (typeof id !== 'string' || typeof createdAt !== 'string' || typeof updatedAt !== 'string') return null
  if (value.classificationMode !== 'shadow' && value.classificationMode !== 'enforce') return null
  return { id, classificationMode: value.classificationMode, createdAt, updatedAt }
}

function normalizeBackupConfigurations(configurations: Record<string, unknown>): ConfigurationBackup['configurations'] {
  return {
    certificates: asRecordArray(configurations.certificates) as unknown as Certificate[],
    hosts: asRecordArray(configurations.hosts).map(normalizeHost).filter((entry): entry is SipHostInfo => entry !== null),
    tlsContexts: asRecordArray(configurations.tlsContexts) as unknown as TlsContext[],
    registeredDeliverySettings: asRecordArray(configurations.registeredDeliverySettings) as unknown as RegisteredDeliverySettings[],
    sipManipulations: asRecordArray(configurations.sipManipulations) as unknown as SipManipulation[],
    codecGroups: asRecordArray(configurations.codecGroups) as unknown as CodecGroup[],
    mediaRealms: asRecordArray(configurations.mediaRealms) as unknown as MediaRealm[],
    sipInterfaces: asRecordArray(configurations.sipInterfaces) as unknown as SipInterface[],
    peerGroups: asRecordArray(configurations.peerGroups) as unknown as PeerGroup[],
    sipProfiles: asRecordArray(configurations.sipProfiles).map(normalizeSipProfile).filter((profile): profile is Sip.SipProfileInfo => profile !== null),
    containerProfiles: asRecordArray(configurations.containerProfiles).map(normalizeContainerProfile).filter((entry): entry is BackupContainerProfileInfo => entry !== null),
    containerProfileSipInterfaces: asRecordArray(configurations.containerProfileSipInterfaces).map(normalizeContainerProfileSipInterface).filter((entry): entry is BackupContainerProfileSipInterface => entry !== null),
    containerProfileAssignments: asRecordArray(configurations.containerProfileAssignments).map(normalizeContainerProfileAssignment).filter((entry): entry is BackupContainerProfileAssignment => entry !== null),
    sipPeers: asRecordArray(configurations.sipPeers) as unknown as SipPeer[],
    routingRules: asRecordArray(configurations.routingRules) as unknown as RoutingRule[],
    sipRoutingSettings: asRecordArray(configurations.sipRoutingSettings).map(normalizeSipRoutingSettings).filter((entry): entry is SipRoutingSettingsInfo => entry !== null),
  }
}

export function validateBackupGraph(backup: unknown): BackupValidationIssue[] {
  const issues: BackupValidationIssue[] = []
  if (!isRecord(backup)) {
    return [{ path: 'backup', message: 'Backup file is not a JSON object' }]
  }

  const version = typeof backup.version === 'string' ? backup.version : null
  if (version === null || !SUPPORTED_BACKUP_FORMAT_VERSIONS.has(version)) {
    issues.push({
      path: 'version',
      message: `Unsupported backup format version ${version ?? 'unknown'}. Supported versions are 1.1.0 through ${CURRENT_BACKUP_FORMAT_VERSION}. Re-export from the current UI before restoring.`,
    })
  }

  const configurations = isRecord(backup.configurations) ? backup.configurations : null
  if (!configurations) {
    issues.push({ path: 'configurations', message: 'Backup is missing configuration collections' })
    return issues
  }

  const normalized = normalizeBackupConfigurations(configurations)
  const hosts = normalized.hosts
  const tlsContexts = normalized.tlsContexts
  const sipManipulations = normalized.sipManipulations
  const sipInterfaces = normalized.sipInterfaces
  const sipProfiles = normalized.sipProfiles
  const containerProfiles = normalized.containerProfiles
  const containerProfileSipInterfaces = normalized.containerProfileSipInterfaces
  const containerProfileAssignments = normalized.containerProfileAssignments
  const sipPeers = normalized.sipPeers
  const sipRoutingSettings = normalized.sipRoutingSettings

  const certificateIds = new Set(normalized.certificates.map((certificate) => certificate.id))
  const hostIds = new Set(hosts.map((host) => host.id))
  const tlsContextIds = new Set(tlsContexts.map((context) => context.id))
  const manipulationIds = new Set(sipManipulations.map((manipulation) => manipulation.id))
  const sipInterfaceIds = new Set(sipInterfaces.map((sipInterface) => sipInterface.id))
  const sipProfileIds = new Set(sipProfiles.map((profile) => profile.id))
  const containerProfileIds = new Set(containerProfiles.map((profile) => profile.id))

  for (const host of hosts) {
    if (host.certificateId !== null && !certificateIds.has(host.certificateId)) {
      issues.push({ path: `hosts.${host.name}.certificateId`, message: `References missing certificate ${host.certificateId}` })
    }
  }

  for (const sipInterface of sipInterfaces) {
    if (sipInterface.protocol === 'TLS' && sipInterface.tlsContextId === null) {
      issues.push({ path: `sipInterfaces.${sipInterface.interfaceId}.tlsContextId`, message: 'TLS SIP interfaces require a tlsContextId' })
    }
    if (typeof sipInterface.tlsContextId === 'string' && !tlsContextIds.has(sipInterface.tlsContextId)) {
      issues.push({ path: `sipInterfaces.${sipInterface.interfaceId}.tlsContextId`, message: `References missing TLS context ${sipInterface.tlsContextId}` })
    }
    if (typeof sipInterface.preClassificationManipulationId === 'string' && !manipulationIds.has(sipInterface.preClassificationManipulationId)) {
      issues.push({ path: `sipInterfaces.${sipInterface.interfaceId}.preClassificationManipulationId`, message: `References missing manipulation profile ${sipInterface.preClassificationManipulationId}` })
    }
    if (typeof sipInterface.hostId === 'string' && !hostIds.has(sipInterface.hostId)) {
      issues.push({ path: `sipInterfaces.${sipInterface.interfaceId}.hostId`, message: `References missing host ${sipInterface.hostId}` })
    }
  }

  for (const profile of containerProfiles) {
    if (profile.mediaRealmId !== null && !normalized.mediaRealms.some((mediaRealm) => mediaRealm.id === profile.mediaRealmId)) {
      issues.push({ path: `containerProfiles.${profile.name}.mediaRealmId`, message: `References missing media realm ${profile.mediaRealmId}` })
    }
  }

  for (const link of containerProfileSipInterfaces) {
    if (!containerProfileIds.has(link.profileId)) {
      issues.push({ path: `containerProfileSipInterfaces.${link.id}.profileId`, message: `References missing container profile ${link.profileId}` })
    }
    if (!sipInterfaceIds.has(link.sipInterfaceId)) {
      issues.push({ path: `containerProfileSipInterfaces.${link.id}.sipInterfaceId`, message: `References missing SIP interface ${link.sipInterfaceId}` })
    }
  }

  for (const assignment of containerProfileAssignments) {
    if (!containerProfileIds.has(assignment.profileId)) {
      issues.push({ path: `containerProfileAssignments.${assignment.id}.profileId`, message: `References missing container profile ${assignment.profileId}` })
    }
  }

  for (const sipPeer of sipPeers) {
    const peerName = typeof sipPeer.name === 'string' ? sipPeer.name : 'unknown'
    const sipInterfaceId = typeof sipPeer.sipInterfaceId === 'string' ? sipPeer.sipInterfaceId : null
    const tlsContextId = typeof sipPeer.tlsContextId === 'string' ? sipPeer.tlsContextId : null
    if (sipInterfaceId !== null && !sipInterfaceIds.has(sipInterfaceId)) {
      issues.push({ path: `sipPeers.${peerName}.sipInterfaceId`, message: `References missing SIP interface ${sipInterfaceId}` })
    }
    if (tlsContextId !== null && !tlsContextIds.has(tlsContextId)) {
      issues.push({ path: `sipPeers.${peerName}.tlsContextId`, message: `References missing TLS context ${tlsContextId}` })
    }
    if (sipProfiles.length > 0 && typeof sipPeer.sipProfileId === 'string' && !sipProfileIds.has(sipPeer.sipProfileId)) {
      issues.push({ path: `sipPeers.${peerName}.sipProfileId`, message: `References missing SIP profile ${sipPeer.sipProfileId}` })
    }
  }

  for (const routingSetting of sipRoutingSettings) {
    if (routingSetting.id !== 'global') {
      issues.push({ path: `sipRoutingSettings.${routingSetting.id}.id`, message: 'SIP routing settings backups must use the global singleton id' })
    }
  }

  return issues
}

// Configuration Backup Service
export class ConfigurationBackupService {
  // Create a full system backup
  async createBackup(options: {
    name: string
    description?: string
    includeTypes?: string[]
    excludeTypes?: string[]
    environment?: string
  }): Promise<ConfigurationBackup> {
    const response = await api.get<ConfigurationBackup>('/api/configuration-backup/export')
    const backup = response.data
    const localBackup: ConfigurationBackup = {
      ...backup,
      name: options.name,
      description: options.description || backup.description,
      environment: options.environment || backup.environment,
      configurations: this.filterConfigurations(backup.configurations, options.includeTypes, options.excludeTypes),
    }
    localBackup.metadata = this.buildBackupMetadata(localBackup.configurations)
    localBackup.size = JSON.stringify(localBackup.configurations).length
    this.storeBackup(localBackup)
    return localBackup
  }

  private buildBackupMetadata(configurations: ConfigurationBackup['configurations']): ConfigurationBackup['metadata'] {
    const totalConfigurations = Object.values(configurations).reduce((sum, configs) => sum + configs.length, 0)
    const configurationTypes = Object.keys(configurations).filter((key) => configurations[key as keyof typeof configurations].length > 0)

    return {
      totalConfigurations,
      configurationTypes,
      presentCollections: collectPresentCollections(configurations as Record<string, unknown>),
      dependencies: this.calculateDependencies(configurations),
      tags: this.generateTags(configurations),
    }
  }

  private normalizeBackupRecord(
    backup: unknown,
    options: { readonly allowGeneratedId: boolean },
  ): ConfigurationBackup {
    if (!isRecord(backup)) {
      throw new Error('Backup file is not a JSON object')
    }

    const version = getStringField(backup, 'version')
    if (version === null || !SUPPORTED_BACKUP_FORMAT_VERSIONS.has(version)) {
      throw new Error(`Unsupported backup format version ${version ?? 'unknown'}. Supported versions are 1.1.0 through ${CURRENT_BACKUP_FORMAT_VERSION}. Re-export from the current UI before restoring.`)
    }

    const configurationsRecord = isRecord(backup.configurations) ? backup.configurations : null
    if (!configurationsRecord) {
      throw new Error('Backup is missing configuration collections')
    }

    const configurations = normalizeBackupConfigurations(configurationsRecord)
    const id = getStringField(backup, 'id')
    if (id === null && !options.allowGeneratedId) {
      throw new Error('Backup is missing an id')
    }

    const createdAt = getStringField(backup, 'createdAt') ?? new Date().toISOString()

    return {
      id: id ?? `imported-${Date.now()}`,
      name: getStringField(backup, 'name') ?? 'Imported backup',
      description: getStringField(backup, 'description') ?? 'Imported backup',
      createdAt,
      createdBy: getStringField(backup, 'createdBy') ?? 'imported',
      version: CURRENT_BACKUP_FORMAT_VERSION,
      environment: getStringField(backup, 'environment') ?? 'production',
      size: typeof backup.size === 'number' ? backup.size : JSON.stringify(configurations).length,
      configurations,
      metadata: { ...this.buildBackupMetadata(configurations), presentCollections: collectPresentCollections(configurationsRecord) },
    }
  }

  // Restore from backup
  async restoreFromBackup(
    backupId: string,
    options: {
      overwriteExisting?: boolean
      includeTypes?: string[]
      excludeTypes?: string[]
      dryRun?: boolean
    } = {}
  ): Promise<RestoreOperation> {
    const backup = await this.getBackup(backupId)
    if (!backup) {
      throw new Error(`Backup ${backupId} not found`)
    }

    const normalizedBackup = this.normalizeBackupRecord(backup, { allowGeneratedId: false })
    const response = await api.post<BackupRestoreReportPayload>('/api/configuration-backup/restore', {
      backup: normalizedBackup,
      dryRun: options.dryRun ?? false,
      confirm: options.dryRun ? undefined : 'APPLY',
      overwriteExisting: options.overwriteExisting ?? true,
      includeTypes: options.includeTypes ?? [],
      excludeTypes: options.excludeTypes ?? [],
    })

    const report = response.data
    const total = report.summary.create + report.summary.update + report.summary.conflict + report.summary.blocked
    return {
      id: `restore-${Date.now()}`,
      backupId,
      startedAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
      status: report.readyToApply && !report.dryRun ? 'completed' : report.blockers.length > 0 ? 'failed' : 'completed',
      progress: 100,
      results: {
        total,
        successful: report.summary.create + report.summary.update,
        failed: report.summary.conflict + report.summary.blocked,
        skipped: 0,
      },
      errors: report.entries.filter((entry) => entry.action === 'blocked' || entry.action === 'conflict').map((entry) => ({ type: entry.collection, item: entry.id, error: entry.reason ?? 'Restore blocked' })),
      options: report.options,
    }
  }

  // List all backups
  async listBackups(): Promise<ConfigurationBackup[]> {
    // In real implementation, this would fetch from backend
    const backups = this.getStoredBackups()
    return backups.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  }

  // Get specific backup
  async getBackup(backupId: string): Promise<ConfigurationBackup | null> {
    const backups = this.getStoredBackups()
    return backups.find(backup => backup.id === backupId) || null
  }

  // Delete backup
  async deleteBackup(backupId: string): Promise<void> {
    const backups = this.getStoredBackups()
    const filteredBackups = backups.filter(backup => backup.id !== backupId)
    localStorage.setItem('sbclite-backups', JSON.stringify(filteredBackups))
  }

  // Export backup to file
  exportBackup(backup: ConfigurationBackup): void {
    const backupJson = JSON.stringify(backup, null, 2)
    const blob = new Blob([backupJson], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `sbclite-backup-${backup.name.replace(/\s+/g, '-')}-${backup.createdAt.split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Import backup from file
  async importBackup(backupJson: string): Promise<ConfigurationBackup> {
    try {
      const parsed: unknown = JSON.parse(backupJson)
      const normalized = this.normalizeBackupRecord(parsed, { allowGeneratedId: true })
      const validationIssues = validateBackupGraph(normalized)
      if (validationIssues.length > 0) {
        throw new Error(validationIssues.map((issue) => `${issue.path}: ${issue.message}`).join('; '))
      }

      // Generate new ID to avoid conflicts
      const backup: ConfigurationBackup = {
        ...normalized,
        id: `imported-${Date.now()}`,
        name: `${normalized.name} (Imported)`,
      }
      
      this.storeBackup(backup)
      return backup
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to parse backup file')
    }
  }

  // Compare two backups
  compareBackups(backup1: ConfigurationBackup, backup2: ConfigurationBackup): {
    added: Record<string, unknown[]>
    removed: Record<string, unknown[]>
    modified: Record<string, unknown[]>
    unchanged: Record<string, unknown[]>
  } {
    const result = {
      added: {} as Record<string, unknown[]>,
      removed: {} as Record<string, unknown[]>,
      modified: {} as Record<string, unknown[]>,
      unchanged: {} as Record<string, unknown[]>
    }

    const configTypes = Object.keys(backup1.configurations) as Array<keyof typeof backup1.configurations>

    for (const configType of configTypes) {
      const configs1 = backup1.configurations[configType] || []
      const configs2 = backup2.configurations[configType] || []

      result.added[configType] = []
      result.removed[configType] = []
      result.modified[configType] = []
      result.unchanged[configType] = []

      // Find added configurations
      for (const config2 of configs2) {
        const id2 = this.getConfigId(config2)
        const found = configs1.find(c1 => this.getConfigId(c1) === id2)
        if (!found) {
          result.added[configType].push(config2)
        }
      }

      // Find removed and modified configurations
      for (const config1 of configs1) {
        const found = configs2.find(c2 => this.getConfigId(c2) === this.getConfigId(config1))
        if (!found) {
          result.removed[configType].push(config1)
        } else {
          // Check if modified
          if (JSON.stringify(config1) !== JSON.stringify(found)) {
            result.modified[configType].push({ old: config1, new: found })
          } else {
            result.unchanged[configType].push(config1)
          }
        }
      }
    }

    return result
  }

  // Schedule automatic backups
  async createBackupSchedule(schedule: Omit<BackupSchedule, 'id' | 'nextRun' | 'status'>): Promise<BackupSchedule> {
    const scheduleId = `schedule-${Date.now()}`
    const nextRun = this.calculateNextRun(schedule.frequency, schedule.time)

    const newSchedule: BackupSchedule = {
      ...schedule,
      id: scheduleId,
      nextRun,
      status: 'active'
    }

    // Store schedule (in real implementation, this would go to backend)
    const schedules = this.getStoredSchedules()
    schedules.push(newSchedule)
    localStorage.setItem('sbclite-backup-schedules', JSON.stringify(schedules))

    return newSchedule
  }

  // Private helper methods
  private filterConfigurations(
    configurations: ConfigurationBackup['configurations'],
    includeTypes?: string[],
    excludeTypes?: string[]
  ): ConfigurationBackup['configurations'] {
    const filtered = { ...configurations }

    if (includeTypes && includeTypes.length > 0) {
      Object.keys(filtered).forEach(key => {
        if (!includeTypes.includes(key)) {
          (filtered as Record<string, unknown[]>)[key] = []
        }
      })
    }

    if (excludeTypes && excludeTypes.length > 0) {
      excludeTypes.forEach(type => {
        if (filtered[type as keyof typeof filtered]) {
          (filtered as Record<string, unknown[]>)[type] = []
        }
      })
    }

    return filtered
  }

  private calculateDependencies(configurations: ConfigurationBackup['configurations']): string[] {
    const dependencies: string[] = []
    const hosts = configurations.hosts ?? []
    const containerProfiles = configurations.containerProfiles ?? []
    const containerProfileSipInterfaces = configurations.containerProfileSipInterfaces ?? []
    const containerProfileAssignments = configurations.containerProfileAssignments ?? []
    
    // Add dependencies based on configuration relationships
    
    if (configurations.sipInterfaces.length > 0) {
      dependencies.push('tlsContexts')
    }
    if (hosts.length > 0) {
      dependencies.push('certificates')
    }
    if (configurations.sipProfiles.length > 0) {
      dependencies.push('sipProfiles')
    }
    if (containerProfiles.length > 0) {
      dependencies.push('mediaRealms')
    }
    if (containerProfileSipInterfaces.length > 0) {
      dependencies.push('containerProfiles')
      dependencies.push('sipInterfaces')
    }
    if (containerProfileAssignments.length > 0) {
      dependencies.push('containerProfiles')
    }
    if (configurations.sipPeers.length > 0) {
      dependencies.push('sipInterfaces')
      dependencies.push('tlsContexts')
      if (configurations.sipProfiles.length > 0) {
        dependencies.push('sipProfiles')
      }
    }
    if (configurations.routingRules.length > 0) {
      dependencies.push('peerGroups')
    }
    if (configurations.sipPeers.length > 0) {
      dependencies.push('mediaRealms')
    }
    if (configurations.sipManipulations.length > 0) {
      dependencies.push('sipManipulations')
    }
    if (configurations.sipInterfaces.some((sipInterface) => typeof sipInterface.preClassificationManipulationId === 'string')) {
      dependencies.push('sipManipulations')
    }
    if (containerProfiles.some((profile) => typeof profile.mediaRealmId === 'string')) {
      dependencies.push('mediaRealms')
    }
    // Note: IP Groups dependency removed - IP filtering is now at peer level

    return [...new Set(dependencies)]
  }

  private generateTags(configurations: ConfigurationBackup['configurations']): string[] {
    const tags: string[] = []
    const hosts = configurations.hosts ?? []
    const containerProfiles = configurations.containerProfiles ?? []
    const containerProfileSipInterfaces = configurations.containerProfileSipInterfaces ?? []
    const containerProfileAssignments = configurations.containerProfileAssignments ?? []
    const sipRoutingSettings = configurations.sipRoutingSettings ?? []
    
    if (configurations.certificates.length > 0) tags.push('security', 'tls')
    if (hosts.length > 0) tags.push('security', 'hosts')
    if (configurations.tlsContexts.length > 0) tags.push('security', 'tls-contexts')
    if (configurations.registeredDeliverySettings.length > 0) tags.push('sip', 'registered-delivery')
    if (configurations.sipManipulations.length > 0) tags.push('routing', 'manipulations')
    if (configurations.codecGroups.length > 0) tags.push('media', 'codecs')
    if (configurations.mediaRealms.length > 0) tags.push('media', 'rtp')
    if (configurations.sipInterfaces.length > 0) tags.push('network', 'sip-interfaces')
    if (configurations.sipProfiles.length > 0) tags.push('sip', 'profiles')
    if (containerProfiles.length > 0 || containerProfileSipInterfaces.length > 0 || containerProfileAssignments.length > 0) tags.push('engine-placement')
    if (configurations.routingRules.length > 0) tags.push('routing', 'rules')
    if (configurations.sipPeers.length > 0) tags.push('sip', 'peers')
    if (sipRoutingSettings.length > 0) tags.push('routing', 'classification')

    return [...new Set(tags)]
  }

  private async checkConfigurationExists(configType: string, config: unknown): Promise<boolean> {
    try {
      if (!isRecord(config)) {
        return false
      }

      switch (configType) {
        case 'certificates': {
          const certs = await configurationApi.getCertificates()
          const id = getStringField(config, 'id')
          const name = getStringField(config, 'name')
          return certs.some((certificate) => certificate.id === id || certificate.name === name)
        }
        case 'tlsContexts': {
          const contexts = await configurationApi.getTlsContexts()
          const id = getStringField(config, 'id')
          const name = getStringField(config, 'name')
          return contexts.some((context) => context.id === id || context.name === name)
        }
        case 'registeredDeliverySettings': {
          const settings = await configurationApi.getRegisteredDeliverySettings()
          return settings !== null
        }
        case 'sipManipulations': {
          const manipulations = await configurationApi.getSipManipulations()
          const id = getStringField(config, 'id')
          const name = getStringField(config, 'name')
          return manipulations.some((manipulation) => manipulation.id === id || manipulation.name === name)
        }
        case 'codecGroups': {
          const codecs = await configurationApi.getCodecGroups()
          const codecId = getStringField(config, 'codecId')
          const name = getStringField(config, 'name')
          return codecs.some((codec) => codec.codecId === codecId || codec.name === name)
        }
        case 'mediaRealms': {
          const realms = await configurationApi.getMediaRealms()
          const id = getStringField(config, 'id')
          const name = getStringField(config, 'name')
          return realms.some((realm) => realm.id === id || realm.name === name)
        }
        case 'sipInterfaces': {
          const interfaces = await configurationApi.getSipInterfaces()
          const id = getStringField(config, 'id')
          const interfaceId = getStringField(config, 'interfaceId')
          return interfaces.some((sipInterface) => sipInterface.id === id || sipInterface.interfaceId === interfaceId)
        }
        case 'peerGroups': {
          const peerGroups = await configurationApi.getPeerGroups()
          const id = getStringField(config, 'id')
          const name = getStringField(config, 'name')
          return peerGroups.some((peerGroup) => peerGroup.id === id || peerGroup.name === name)
        }
        case 'sipPeers': {
          const sipPeers = await configurationApi.getSipPeers()
          const id = getStringField(config, 'id')
          const name = getStringField(config, 'name')
          return sipPeers.some((sipPeer) => sipPeer.id === id || sipPeer.name === name)
        }
        case 'routingRules': {
          const rules = await configurationApi.getRoutingRules()
          const routeId = getStringField(config, 'routeId')
          const ruleId = getStringField(config, 'ruleId')
          const name = getStringField(config, 'name')
          return rules.some((rule) => (rule.routeId === routeId && rule.ruleId === ruleId) || rule.name === name)
        }
        default:
          return false
      }
    } catch {
      return false
    }
  }

  private async restoreConfiguration(configType: string, config: unknown, exists: boolean): Promise<void> {
    if (!isRecord(config)) {
      throw new Error(`Invalid ${configType} backup entry`)
    }

    switch (configType) {
      case 'certificates':
        {
        const id = getStringField(config, 'id')
        if (!id) throw new Error('Certificate backup entry is missing an id')
        if (exists) {
          await configurationApi.updateCertificate(id, config as Partial<Certificate>)
        } else {
          await configurationApi.createCertificate(config as Omit<Certificate, 'id'>)
        }
        break
        }
      case 'tlsContexts':
        {
        const id = getStringField(config, 'id')
        if (!id) throw new Error('TLS context backup entry is missing an id')
        if (exists) {
          await configurationApi.updateTlsContext(id, config as Partial<TlsContext>)
        } else {
          await configurationApi.createTlsContext(config as Partial<TlsContext>)
        }
        break
        }
      case 'registeredDeliverySettings': {
        const id = getStringField(config, 'id')
        if (!id) throw new Error('Registered delivery settings backup entry is missing an id')
        const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...payload } = config as RegisteredDeliverySettings
        await configurationApi.updateRegisteredDeliverySettings(payload)
        break
      }
      case 'sipManipulations': {
        const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...payload } = config as SipManipulation
        const id = getStringField(config, 'id')
        if (!id) throw new Error('SIP manipulation backup entry is missing an id')
        if (exists) {
          await configurationApi.updateSipManipulation(id, payload)
        } else {
          const { description: _description, ...createPayload } = payload
          await configurationApi.createSipManipulation(createPayload)
        }
        break
      }
      case 'codecGroups':
        {
        const id = getStringField(config, 'id')
        if (!id) throw new Error('Codec group backup entry is missing an id')
        if (exists) {
          await configurationApi.updateCodecGroup(id, config as Partial<CodecGroup>)
        } else {
          await configurationApi.createCodecGroup(config as Omit<CodecGroup, 'codecId'>)
        }
        break
        }
      case 'mediaRealms':
        {
          const id = getStringField(config, 'id')
        const realmId = getStringField(config, 'realmId') ?? id
        if (!realmId) throw new Error('Media realm backup entry is missing an id')
        if (exists) {
          await configurationApi.updateMediaRealm(realmId, config as Partial<MediaRealm>)
        } else {
          await configurationApi.createMediaRealm(config as Omit<MediaRealm, 'realmId'>)
        }
        break
        }
      case 'sipInterfaces': {
        const {
          id: _id,
          timeCreated: _timeCreated,
          timeUpdated: _timeUpdated,
          maxConnections: _maxConnections,
          maxConcurrentCalls: _maxConcurrentCalls,
          ...payload
        } = config as SipInterface
        const id = getStringField(config, 'id')
        if (!id) throw new Error('SIP interface backup entry is missing an id')
        const normalizedPayload = {
          ...payload,
          ...(_maxConnections === null ? {} : { maxConnections: _maxConnections }),
          ...(_maxConcurrentCalls === null ? {} : { maxConcurrentCalls: _maxConcurrentCalls }),
        }
        if (exists) {
          await configurationApi.updateSipInterface(id, normalizedPayload)
        } else {
          await configurationApi.createSipInterface(normalizedPayload)
        }
        break
      }
      case 'peerGroups':
        {
        const id = getStringField(config, 'id')
        if (!id) throw new Error('Peer group backup entry is missing an id')
        if (exists) {
          await configurationApi.updatePeerGroup(id, config as Partial<PeerGroup>)
        } else {
          await configurationApi.createPeerGroup(config as Omit<PeerGroup, 'id'>)
        }
        break
        }
      case 'sipPeers': {
        // SIP peers cannot be created with a specific UUID id. Upsert by id if present, otherwise by name.
        const current = await configurationApi.getSipPeers()
        const id = getStringField(config, 'id')
        const name = getStringField(config, 'name')
        const existing = current.find((p) => p.id === id) || current.find((p) => p.name === name)
        if (existing) {
          await configurationApi.updateSipPeer(existing.id, config as Sip.PeerUpdateInput)
        } else {
          await configurationApi.createSipPeer(config as Sip.PeerCreateInput)
        }
        break
      }
      case 'routingRules':
        {
          const routeId = getStringField(config, 'routeId')
          const ruleId = getStringField(config, 'ruleId')
          if (!routeId) throw new Error('Routing rule backup entry is missing a routeId')
          if (!ruleId) throw new Error('Routing rule backup entry is missing a ruleId')
        if (exists) {
          await configurationApi.updateRoutingRule(routeId, ruleId, config as Partial<RoutingRule>)
        } else {
          await configurationApi.createRoutingRule(config as Omit<RoutingRule, 'routeId' | 'ruleId'>)
        }
        break
        }
    }
  }

  private getConfigId(config: unknown): string {
    if (!isRecord(config)) return 'unknown'
    // Handle different configuration types with their specific ID fields
    if (typeof config.realmId === 'string') return config.realmId // MediaRealm
    if (typeof config.routeId === 'string' && typeof config.ruleId === 'string') return `${config.routeId}-${config.ruleId}` // RoutingRule
    if (typeof config.containerId === 'string' && typeof config.profileId === 'string') return `${config.containerId}-${config.profileId}` // Container profile assignment
    if (typeof config.profileId === 'string' && typeof config.sipInterfaceId === 'string') return `${config.profileId}-${config.sipInterfaceId}` // Container profile SIP interface
    if (typeof config.classificationMode === 'string') return config.classificationMode // SIP routing singleton
    if (typeof config.certificateId === 'string') return config.certificateId // Host / TLS cert-backed items
    if (typeof config.interfaceId === 'string') return config.interfaceId // SIP interface
    if (typeof config.id === 'string') return config.id // Most other types
    if (typeof config.name === 'string') return config.name // Fallback to name
    return 'unknown'
  }

  private storeBackup(backup: ConfigurationBackup): void {
    const backups = this.getStoredBackups()
    backups.push(this.normalizeBackupRecord(backup, { allowGeneratedId: true }))
    localStorage.setItem('sbclite-backups', JSON.stringify(backups))
  }

  private getStoredBackups(): ConfigurationBackup[] {
    try {
      const stored = localStorage.getItem('sbclite-backups')
      if (!stored) {
        return []
      }

      const parsed: unknown = JSON.parse(stored)
      if (!Array.isArray(parsed)) {
        return []
      }

      const { backups, changed } = parsed.reduce(
        (result, value) => {
          try {
            const normalized = this.normalizeBackupRecord(value, { allowGeneratedId: false })
            result.backups.push(normalized)
            if (JSON.stringify(value) !== JSON.stringify(normalized)) {
              result.changed = true
            }
          } catch (error) {
            result.changed = true
            console.warn('Dropping invalid stored backup during migration', {
              error: error instanceof Error ? error.message : String(error),
            })
          }

          return result
        },
        { backups: [] as ConfigurationBackup[], changed: false },
      )

      if (changed) {
        localStorage.setItem('sbclite-backups', JSON.stringify(backups))
      }

      return backups
    } catch {
      return []
    }
  }

  private getStoredSchedules(): BackupSchedule[] {
    try {
      const stored = localStorage.getItem('sbclite-backup-schedules')
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  }

  private calculateNextRun(frequency: BackupSchedule['frequency'], time: string): string {
    const now = new Date()
    const [hours, minutes] = time.split(':').map(Number)
    
    const nextRun = new Date(now)
    nextRun.setHours(hours, minutes, 0, 0)
    
    switch (frequency) {
      case 'hourly':
        if (nextRun <= now) {
          nextRun.setHours(nextRun.getHours() + 1)
        }
        break
      case 'daily':
        if (nextRun <= now) {
          nextRun.setDate(nextRun.getDate() + 1)
        }
        break
      case 'weekly':
        if (nextRun <= now) {
          nextRun.setDate(nextRun.getDate() + 7)
        }
        break
      case 'monthly':
        if (nextRun <= now) {
          nextRun.setMonth(nextRun.getMonth() + 1)
        }
        break
    }
    
    return nextRun.toISOString()
  }
}

// Export singleton instance
export const backupService = new ConfigurationBackupService()
